/**
 * 添付ファイルの共通入力(2026-08-26)。全画面の添付欄をこれに統一する:
 * クリック/タップで選択 / ドラッグ&ドロップ / Ctrl+V 貼り付け。
 * スマホはOSの選択画面(カメラ/ファイル等)がそのまま開く(2026-08-28)。
 * 選択済みはサムネイル表示(クリックで拡大・削除可)
 */
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import Modal from '@core/components/Modal';
import ZoomableImage from '@core/components/ZoomableImage';
import { useToast } from '@core/components/Toast';
import { todayStr } from '@core/lib/date';
import { validateFile } from './attachments';

export default function FileDropZone({
  files,
  onChange,
}: {
  files: File[];
  onChange: (files: File[]) => void;
}) {
  const toast = useToast();
  const [dragging, setDragging] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ヒント文の出し分け用(主入力がタッチ=pointer: coarse)。
  // タッチ対応ノートPCでも変えないため、maxTouchPointsでは判定しない(2026-08-26)
  const isTouchDevice = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
    [],
  );

  const previews = useMemo(
    () => files.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null)),
    [files],
  );
  useEffect(() => {
    return () => previews.forEach((u) => u && URL.revokeObjectURL(u));
  }, [previews]);

  const addFiles = useCallback(
    (added: File[]) => {
      const bad = added.map(validateFile).find((e) => e);
      if (bad) {
        toast(bad);
        return;
      }
      if (added.length > 0) onChange([...files, ...added]);
    },
    [files, onChange, toast],
  );

  // ペースト(スクショ等の画像)。マウント中のみ拾う
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const items = Array.from(e.clipboardData?.items ?? []);
      const images = items
        .filter((i) => i.kind === 'file')
        .map((i) => i.getAsFile())
        .filter((f): f is File => !!f)
        .map(
          (f, idx) =>
            new File([f], f.name === 'image.png' ? `貼り付け_${todayStr()}_${idx + 1}.png` : f.name, {
              type: f.type,
            }),
        );
      if (images.length > 0) addFiles(images);
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [addFiles]);

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    addFiles(Array.from(e.dataTransfer?.files ?? []));
  }

  return (
    <>
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragging ? 'var(--brand-blue)' : 'var(--line)'}`,
          background: dragging ? 'var(--blue-bg)' : '#FAFBFD',
          borderRadius: 8,
          padding: '12px 12px',
          cursor: 'pointer',
          textAlign: 'center',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
        <div className="hint">
          {isTouchDevice
            ? 'タップして添付(カメラ・写真・ファイル)'
            : 'クリックしてファイルを選択、ここにドラッグ&ドロップ、またはスクリーンショットを Ctrl+V で貼り付け'}
        </div>
        {/* 自作の選択シートは廃止(2026-08-28 指示): 枠タップで直接OSの選択画面
            (カメラ/ファイル。端末により写真も)を開く。表示される項目はOS側が決める */}
        {files.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10, justifyContent: 'flex-start' }}>
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} style={{ width: 92, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                {previews[i] ? (
                  <img
                    src={previews[i] ?? undefined}
                    alt={f.name}
                    title="クリックで拡大"
                    onClick={() => setPreviewIndex(i)}
                    style={{
                      width: 92,
                      height: 68,
                      objectFit: 'cover',
                      borderRadius: 6,
                      border: '1px solid var(--line)',
                      background: '#fff',
                      cursor: 'zoom-in',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 92,
                      height: 68,
                      borderRadius: 6,
                      border: '1px solid var(--line)',
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      color: 'var(--muted)',
                    }}
                  >
                    PDF
                  </div>
                )}
                <div
                  className="hint"
                  style={{ fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={f.name}
                >
                  {f.name}
                </div>
                <button
                  type="button"
                  className="txn-link"
                  style={{ color: 'var(--red)', fontSize: 11 }}
                  onClick={() => onChange(files.filter((_, j) => j !== i))}
                >
                  削除
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {previewIndex !== null && files[previewIndex] && previews[previewIndex] && (
        /* スマホは全画面表示(2026-08-31 指示。枠内に小さく収まらないように) */
        <Modal onClose={() => setPreviewIndex(null)} width="min(880px, 94vw)" fullscreen={isTouchDevice}>
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
            <h3 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: isTouchDevice ? 13 : undefined }}>
              {files[previewIndex].name}
            </h3>
          </div>
          {/* ピンチ・ホイール・ダブルタップで拡大縮小できる(2026-08-28 スマホ改修)。
              全画面時は残り高さいっぱいに収める(2026-08-31) */}
          <div className="preview-fill" style={{ display: 'flex', minHeight: 0 }}>
            <ZoomableImage
              src={previews[previewIndex] ?? ''}
              alt={files[previewIndex].name}
              maxHeight={isTouchDevice ? '100%' : 'calc(100vh - 170px)'}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
